import { Link } from "react-router-dom";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Users, Trophy, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

function CodeModal({ onClose }) {
  const [code, setCode] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (c.length === 6) nav(`/join/${c}`);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-6" onClick={onClose}>
      <motion.form
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-3xl border border-white/10 bg-[var(--surface)] p-8 text-center"
      >
        <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">join lobby</div>
        <h3 className="text-2xl font-bold mt-2 mb-4">Got a 6-character code?</h3>
        <input
          autoFocus
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
          placeholder="ABC123"
          data-testid="landing-code-input"
          className="w-full px-4 py-4 rounded-xl bg-black border border-white/10 focus:border-[var(--accent)] outline-none font-mono text-center tracking-[0.4em] text-2xl"
        />
        <button
          type="submit"
          disabled={code.length !== 6}
          data-testid="landing-code-submit"
          className="mt-4 w-full px-5 py-3 rounded-full bg-[var(--accent)] text-black font-bold disabled:opacity-40"
        >
          Continue →
        </button>
      </motion.form>
    </div>
  );
}

function FloatingCard({ delay = 0, x = 0, rot = 0, color = "#A50044", initials = "RM", years = "2014–2018" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.7 }}
      style={{ transform: `translateX(${x}px) rotate(${rot}deg)` }}
      className="floaty rounded-2xl border border-white/10 bg-gradient-to-b from-[var(--surface-2)] to-black p-4 w-40 sm:w-44 backdrop-blur-md shadow-[0_20px_40px_-20px_rgba(0,255,135,0.3)]"
    >
      <div className="flex items-start justify-between">
        <div className="text-2xl font-black text-[var(--accent)]">9{Math.floor(Math.random() * 9)}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{initials}</div>
      </div>
      <div className="mx-auto my-3 w-16 h-16 rounded-full bg-[var(--accent)]/10 border border-[var(--accent)]/30 flex items-center justify-center text-2xl font-black text-[var(--accent)]/70">
        ?
      </div>
      <div className="text-[11px] font-extrabold uppercase tracking-wider text-center text-zinc-200">
        teammate
      </div>
      <div className="mt-2 flex items-center gap-2 px-2 py-1.5 rounded-md bg-black/40 border border-white/5">
        <span className="w-4 h-4 rounded-full" style={{ background: color }} />
        <span className="text-[10px] font-bold uppercase">{initials}</span>
        <span className="text-[9px] text-zinc-500 ml-auto tabular-nums">{years}</span>
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const { isAuthenticated } = useAuth();
  const [showCode, setShowCode] = useState(false);
  return (
    <div data-testid="landing-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col relative overflow-hidden">
      <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
      <div className="absolute top-1/4 -right-32 w-96 h-96 rounded-full bg-[var(--accent)]/8 blur-3xl pointer-events-none" />

      <header className="relative px-6 py-5 flex items-center justify-between max-w-6xl w-full mx-auto">
        <Logo size={36} />
        <nav className="flex items-center gap-3 text-sm">
          <Link to="/ranked" className="text-zinc-300 hover:text-white transition">Ranked</Link>
          {isAuthenticated ? (
            <Link to="/dashboard" data-testid="nav-dashboard" className="px-4 py-2 rounded-full bg-[var(--accent)] text-black font-semibold hover:brightness-110 transition">
              Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-zinc-300 hover:text-white transition">Log in</Link>
              <Link to="/register" data-testid="nav-register" className="px-4 py-2 rounded-full bg-[var(--accent)] text-black font-semibold hover:brightness-110 transition">
                Create account
              </Link>
            </>
          )}
        </nav>
      </header>

      <main className="relative flex-1 max-w-6xl w-full mx-auto px-6 py-12 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--accent)] animate-pulse" />
            real-time multiplayer
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-5 leading-[1.05]">
            Guess the teammate.<br />
            Be the <span className="relative inline-block">
              <span className="text-[var(--accent)]">fastest</span>
              <span className="absolute left-0 right-0 -bottom-2 h-1.5 bg-[var(--accent)] rounded-full" />
            </span>.<br />
            Win the lobby.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md text-base leading-relaxed">
            Three Icon-style cards. One connector. Built on a curated graph of football's biggest transfers since 1995. Play head-to-head with friends, settle the GOAT debate one round at a time.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to={isAuthenticated ? "/dashboard" : "/register"} data-testid="cta-primary" className="group px-6 py-3.5 rounded-full bg-[var(--accent)] text-black font-bold hover:brightness-110 transition inline-flex items-center gap-2 shadow-[0_8px_30px_-8px_var(--accent)]">
              {isAuthenticated ? "Open dashboard" : "Play now"}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <button onClick={() => setShowCode(true)} data-testid="cta-code" className="px-6 py-3.5 rounded-full border border-white/15 text-zinc-200 hover:bg-white/5 transition">
              I have a code
            </button>
          </div>
        </motion.div>

        <div className="relative h-[420px] hidden lg:block">
          <div className="absolute top-4 left-0"><FloatingCard delay={0.1} rot={-8} color="#FEBE10" initials="RM" years="2014–2018" /></div>
          <div className="absolute top-16 left-44"><FloatingCard delay={0.25} rot={4} color="#DA020E" initials="MU" years="2003–2009" /></div>
          <div className="absolute top-44 left-20"><FloatingCard delay={0.4} rot={-3} color="#034694" initials="CH" years="2011–2014" /></div>
        </div>
      </main>

      {/* features */}
      <section className="relative max-w-6xl w-full mx-auto px-6 pb-14 grid sm:grid-cols-3 gap-3">
        {[
          [Zap, "Real-time multiplayer", "WebSocket-driven rounds with sub-second scoring and live leaderboard."],
          [Users, "200+ legendary players", "Hand-curated graph from 1995 to 2024 across the world's elite clubs."],
          [Trophy, "Icon-style cards", "Gothic-shield silhouette with neon green trim. Position chips, nationality flags, club badges."],
        ].map(([Icon, title, body], i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="rounded-2xl border border-white/10 bg-[var(--surface)]/60 p-5"
          >
            <Icon className="text-[var(--accent)] mb-3" size={22} />
            <div className="font-bold text-base">{title}</div>
            <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{body}</div>
          </motion.div>
        ))}
      </section>

      <footer className="relative border-t border-white/5 px-6 py-5 text-xs text-zinc-500 flex items-center justify-between max-w-6xl w-full mx-auto">
        <span>FootGuesser — the teammate guessing game</span>
        <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest">v0.1 MVP</span>
      </footer>

      {showCode && <CodeModal onClose={() => setShowCode(false)} />}
    </div>
  );
}
